'use client'

/**
 * useAssistant — feature hook for the in-app chat assistant.
 *
 * Co-located with assistant components (not in src/hooks/ which is
 * for cross-cutting, feature-agnostic hooks).
 *
 * Exposes:
 *   conversations        — list of recent conversations for the current org/user
 *   conversationData     — messages + conversation doc for the active conversation
 *   sendMessage          — send a user message (returns assistantMessageId)
 *   newConversation      — create a fresh conversation and make it active
 *   cancelMessage        — stop a streaming assistant response
 *   archiveConversation  — soft-archive a conversation (removes it from the list)
 *   retryMessage         — reset a failed assistant message and regenerate
 *   isStreaming          — true when the latest assistant message is streaming
 *   streamingMessageId   — id of the currently-streaming assistant message (or null)
 */

import { useCallback, useMemo } from 'react'
import { useConvexAuth, useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'

interface UseAssistantOptions {
  activeConversationId: Id<'assistantConversations'> | null
}

export function useAssistant({ activeConversationId }: UseAssistantOptions) {
  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth()

  // List recent active conversations — skip when unauthenticated.
  const conversations = useQuery(
    api.features.assistant.queries.listConversations,
    isAuthenticated ? { onlyActive: true, limit: 20 } : 'skip',
  )

  // Full conversation + messages — skip when no active conversation.
  const conversationData = useQuery(
    api.features.assistant.queries.getConversation,
    isAuthenticated && activeConversationId != null
      ? { conversationId: activeConversationId }
      : 'skip',
  )

  // Mutations
  const createConversationMutation = useMutation(
    api.features.assistant.mutations.createConversation,
  )
  const sendMessageMutation = useMutation(
    api.features.assistant.mutations.sendMessage,
  )
  const cancelMessageMutation = useMutation(
    api.features.assistant.mutations.cancelMessage,
  )
  const archiveConversationMutation = useMutation(
    api.features.assistant.mutations.archiveConversation,
  )
  const retryMessageMutation = useMutation(
    api.features.assistant.mutations.retryMessage,
  )
  // generate runs as a client-invoked action (NOT via the Convex scheduler), so
  // it executes on demand and can't be starved by a scheduled-function backlog.
  const generateAction = useAction(api.features.assistant.run.generate)

  // Derive streaming state from the active conversation's messages.
  const streamingMessageId = useMemo<Id<'assistantMessages'> | null>(() => {
    if (!conversationData?.messages) return null
    const streaming = [...conversationData.messages]
      .reverse()
      .find((m) => m.role === 'assistant' && m.status === 'streaming')
    return streaming ? streaming._id : null
  }, [conversationData?.messages])

  const isStreaming = streamingMessageId != null

  // newConversation: creates a conversation and returns its id.
  const newConversation = useCallback(
    async (title?: string) => {
      const result = await createConversationMutation({ title })
      return result.conversationId
    },
    [createConversationMutation],
  )

  // sendMessage: create the rows, then fire the generate action directly.
  // We DON'T await generate (it runs the whole streamed answer, minutes long);
  // the UI follows progress via the reactive getConversation subscription. Any
  // failure is written to the message row server-side (fail()), so we just guard
  // against an unhandled rejection. Returns the assistantMessageId.
  const sendMessage = useCallback(
    async (conversationId: Id<'assistantConversations'>, content: string) => {
      const result = await sendMessageMutation({ conversationId, content })
      void generateAction({ assistantMessageId: result.assistantMessageId }).catch(
        () => {},
      )
      return result.assistantMessageId
    },
    [sendMessageMutation, generateAction],
  )

  // cancelMessage: sets cancelledAt on the streaming message.
  const cancelMessage = useCallback(
    async (messageId: Id<'assistantMessages'>) => {
      await cancelMessageMutation({ messageId })
    },
    [cancelMessageMutation],
  )

  // archiveConversation: soft-archive (drops out of the active list).
  const archiveConversation = useCallback(
    async (conversationId: Id<'assistantConversations'>) => {
      await archiveConversationMutation({ conversationId, isArchived: true })
    },
    [archiveConversationMutation],
  )

  // retryMessage: reset a failed assistant message, then re-fire generate
  // directly (same client-invoked pattern as sendMessage).
  const retryMessage = useCallback(
    async (messageId: Id<'assistantMessages'>) => {
      await retryMessageMutation({ messageId })
      void generateAction({ assistantMessageId: messageId }).catch(() => {})
    },
    [retryMessageMutation, generateAction],
  )

  return {
    conversations,
    conversationData,
    sendMessage,
    newConversation,
    cancelMessage,
    archiveConversation,
    retryMessage,
    isStreaming,
    streamingMessageId,
  }
}
